import { supabase } from "../lib/supabaseClient";
import { getOrCreateAnonId } from "./auraIdentity";

/**
 * Couple Authentication Service
 * Handles signup, login, logout, and session management for couples
 * All functions return { data, error } tuple pattern
 */

/**
 * Claim this device's anonymous Aura profile into the couple account, so the
 * rich behavioural/style identity Aura already built stops being orphaned at
 * signup. Idempotent, safe to run on every signup AND login, on every device.
 * Non-blocking: never fails authentication. (Bride Identity Master, the linchpin.)
 */
async function claimAuraProfile(coupleId) {
  try {
    if (!coupleId) return;
    const anonId = getOrCreateAnonId();
    if (!anonId) return;
    await supabase.rpc("claim_aura_profile_for_couple", { p_anonymous_id: anonId, p_couple_id: coupleId });
  } catch { /* non-blocking */ }
}

// Capture the current anonymous auth uid (if any) BEFORE an auth switch, so we
// can claim its in-progress Wedding into the account afterwards.
async function currentAnonUid() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.is_anonymous ? session.user.id : null;
  } catch { return null; }
}

// Reassign an anonymous Wedding to the now-authenticated couple. Non-blocking;
// the RPC no-ops safely if the caller is still the anon user (wrong session).
async function claimAnonymousWedding(prevAnonUid) {
  try {
    if (!prevAnonUid) return;
    await supabase.rpc("claim_anonymous_wedding", { p_anon_uid: prevAnonUid });
  } catch { /* non-blocking */ }
}

// Migrate any boutique likes/saves that were stored anonymously in localStorage
// into couple_saved_listings. Idempotent via ON CONFLICT DO NOTHING.
async function migrateBoutiqueSavesToDB(coupleId) {
  try {
    if (!coupleId || typeof localStorage === 'undefined') return;
    const seen = new Set();
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const m = key.match(/^lwd_boutique_(?:likes|saves)_([0-9a-f-]{36})$/);
      if (!m) continue;
      const listingId = m[1];
      if (seen.has(listingId)) continue;
      try {
        const val = JSON.parse(localStorage.getItem(key) || '[]');
        if (Array.isArray(val) && val.length > 0) {
          seen.add(listingId);
          entries.push({ couple_id: coupleId, listing_id: listingId, context: 'fashion_house' });
        }
      } catch { /* malformed entry, skip */ }
    }
    if (!entries.length) return;
    await supabase.from('couple_saved_listings')
      .upsert(entries, { onConflict: 'couple_id,listing_id,context' });
  } catch { /* non-blocking */ }
}

/**
 * Sign up a new couple account
 * Creates Supabase Auth user and couple record
 */
export const signupCouple = async (email, password, firstName, lastName, eventDate, guestCount) => {
  try {
    // Capture any in-progress anonymous Wedding BEFORE signUp switches the session.
    const prevAnonUid = await currentAnonUid();

    // 1. Create Supabase Auth user
    // redirectTo points to confirmation page which handles email verification token
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        redirectTo: `${window.location.origin}/getting-married/confirm-email`,
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });

    if (authError) return { data: null, error: authError.message };
    if (!authData.user) return { data: null, error: "Failed to create auth user" };

    // 2. Create couple record in database
    const { data: coupleData, error: coupleError } = await supabase
      .from("couples")
      .insert([
        {
          user_id: authData.user.id,
          email,
          first_name: firstName,
          last_name: lastName,
          event_date: eventDate || null,
          guest_count: guestCount || null,
        },
      ])
      .select()
      .single();

    if (coupleError) {
      // If couple record fails, delete auth user
      await supabase.auth.admin.deleteUser(authData.user.id);
      return { data: null, error: coupleError.message };
    }

    // Claim the anonymous Aura profile into the new account (linchpin).
    await claimAuraProfile(coupleData.id);
    // Carry the in-progress Wedding over to the new account (if signUp already
    // switched the session; the login step below covers the confirm-required case).
    await claimAnonymousWedding(prevAnonUid);
    // Migrate any boutique likes/saves from this device into the new account.
    await migrateBoutiqueSavesToDB(coupleData.id);

    return { data: coupleData, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Log in with email and password
 * Returns couple record on success
 */
export const loginCouple = async (email, password) => {
  try {
    // Capture any in-progress anonymous Wedding BEFORE the session switches.
    const prevAnonUid = await currentAnonUid();

    // 1. Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) return { data: null, error: authError.message };
    if (!authData.user) return { data: null, error: "Authentication failed" };

    // Carry the in-progress anonymous Wedding over to this account.
    await claimAnonymousWedding(prevAnonUid);

    // 2. Fetch couple record
    const couple = await getCurrentCouple();
    // Claim this device's anonymous Aura profile on every login (idempotent).
    if (couple?.data?.id) {
      await claimAuraProfile(couple.data.id);
      // Migrate any boutique likes/saves from this device into the account.
      await migrateBoutiqueSavesToDB(couple.data.id);
    }
    return couple;
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Log out the current couple
 * Clears Supabase session
 */
export const logoutCouple = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) return { data: null, error: error.message };
    return { data: { success: true }, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Get the currently authenticated couple
 * Returns couple record linked to auth user
 */
export const getCurrentCouple = async () => {
  try {
    // 1. Get current auth user
    const { data: authData, error: authError } = await supabase.auth.getUser();

    if (authError) return { data: null, error: authError.message };
    if (!authData.user) return { data: null, error: "Not authenticated" };

    // 2. Fetch couple record
    const { data: coupleData, error: coupleError } = await supabase
      .from("couples")
      .select("*")
      .eq("user_id", authData.user.id)
      .maybeSingle();

    if (coupleError) return { data: null, error: coupleError.message };
    if (!coupleData) return { data: null, error: "Couple record not found" };

    return { data: coupleData, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Get current auth session
 * Returns session object or null if not authenticated
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) return { data: null, error: error.message };
    return { data: data.session, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Listen for auth state changes
 * Calls callback whenever auth state changes (login, logout, token refresh)
 * Returns unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });

  return subscription?.unsubscribe || (() => {});
};

/**
 * Update couple profile information
 */
export const updateCoupleProfile = async (coupleId, updates) => {
  try {
    const { data, error } = await supabase
      .from("couples")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", coupleId)
      .select()
      .single();

    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Request password reset for couple
 * Sends reset link to couple email
 * @param {string} email - Couple email
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const resetPasswordForEmail = async (email) => {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/getting-married/reset-password`,
    });

    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

/**
 * Update password with reset token
 * Called after user clicks reset link in email
 * @param {string} newPassword - New password to set
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const resetPassword = async (newPassword) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};
